import { useContext, useEffect, useRef, useState } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";

type Rect = { x: number; y: number; width: number; height: number };

const ROTATE_STEP = Math.PI / 12; // 15° ずつ回転
// 枠からボタンを離すマージン [px]。
const GAP = 14;

/**
 * IFC 配置オブジェクトの選択枠に追従する overlay gizmo。
 * - 選択中のオブジェクトの bounding box を画面に投影し (viewer.getDesignObjectScreenRect)、
 *   その枠の上下左右に移動ボタン、角に回転ボタンを配置する。
 * - 移動 / 回転は requestAnimationFrame で枠位置を毎フレーム追従させる。
 * design データには反映せず、状態も保存しない (モック)。
 */
export const IfcObjectGizmo = () => {
  const theme = useTheme();
  const { viewer, currentViewerMode } = useContext(ProjectDetailContext);

  const [rect, setRect] = useState<Rect | null>(null);
  const rafRef = useRef<number | null>(null);

  // HTML ボタン gizmo は 3D ギズモ (DesignGizmo3D: 回転リング) へ置き換えたため無効化。
  // 回転は 3D リング (+ 右ドラッグ)、移動は本体ドラッグで行う。
  const active = false && currentViewerMode === EViewerMode.IFC_PLACEMENT;

  useEffect(() => {
    if (!viewer || !active) {
      setRect(null);
      return;
    }

    let prev: Rect | null = null;
    const tick = () => {
      const r = viewer.getDesignObjectScreenRect();
      // 値が変わった時だけ setState して再レンダリングを抑える。
      if (
        !r ||
        !prev ||
        Math.abs(r.x - prev.x) > 0.5 ||
        Math.abs(r.y - prev.y) > 0.5 ||
        Math.abs(r.width - prev.width) > 0.5 ||
        Math.abs(r.height - prev.height) > 0.5
      ) {
        prev = r;
        setRect(r);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setRect(null);
    };
  }, [viewer, active]);

  if (!viewer || !rect) return null;

  const nudge = (dx: number, dy: number) => viewer.nudgeDesignObject(dx, dy);
  const rotate = (rad: number) => viewer.rotateDesignObjectZ(rad);

  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;

  const btnSx = {
    position: "fixed",
    transform: "translate(-50%, -50%)",
    color: theme.palette.base.contrast,
    background: theme.palette.base.modal,
    border: `1px solid ${theme.palette.base.border}`,
    borderRadius: "50%",
    padding: "4px",
    pointerEvents: "auto",
    zIndex: 1300,
    "&:hover": { background: theme.palette.base.modal },
  } as const;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 1300,
      }}
    >
      {/* 選択枠 (画面投影 AABB) は非表示。移動 / 回転 / スケール操作は維持する。 */}

      {/* 上 */}
      <IconButton
        size="small"
        sx={{ ...btnSx, left: cx, top: rect.y - GAP }}
        onClick={() => nudge(0, 1)}
      >
        <ArrowUpwardIcon sx={{ fontSize: 18 }} />
      </IconButton>
      {/* 下 */}
      <IconButton
        size="small"
        sx={{ ...btnSx, left: cx, top: rect.y + rect.height + GAP }}
        onClick={() => nudge(0, -1)}
      >
        <ArrowDownwardIcon sx={{ fontSize: 18 }} />
      </IconButton>
      {/* 左 */}
      <IconButton
        size="small"
        sx={{ ...btnSx, left: rect.x - GAP, top: cy }}
        onClick={() => nudge(-1, 0)}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      {/* 右 */}
      <IconButton
        size="small"
        sx={{ ...btnSx, left: rect.x + rect.width + GAP, top: cy }}
        onClick={() => nudge(1, 0)}
      >
        <ArrowForwardIcon sx={{ fontSize: 18 }} />
      </IconButton>
      {/* 回転 (右上角)。右ドラッグでも回転できる。 */}
      <IconButton
        size="small"
        sx={{
          ...btnSx,
          left: rect.x + rect.width + GAP,
          top: rect.y - GAP,
        }}
        onClick={() => rotate(ROTATE_STEP)}
      >
        <RotateRightIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};
